/*
 * Shared, dependency-free field validators for the organization/team forms. Each returns an error
 * STRING (shown under the field) or null when valid/empty. Empty is treated as valid where the field
 * is optional; the caller decides which fields are required. These mirror the server-side checks in
 * `teams.ts` so the UI gives immediate feedback, but the server remains the authority.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^https?:\/\/[^\s.]+\.[^\s]{2,}$/i;
// A permissive international phone shape: digits, spaces, and + ( ) - . , 7–20 chars of content.
const PHONE_RE = /^[+]?[0-9().\-\s]{7,20}$/;

/** A required non-empty text field, capped at `max`. */
export function validateRequiredText(value: string, label: string, max = 256): string | null {
  const v = value.trim();
  if (v.length === 0) return `${label} is required.`;
  if (v.length > max) return `${label} must be ${max} characters or fewer.`;
  return null;
}

/** An optional free-text field, capped at `max`. */
export function validateOptionalText(value: string, label: string, max = 256): string | null {
  const v = value.trim();
  if (v.length > max) return `${label} must be ${max} characters or fewer.`;
  return null;
}

/** An email; `required` controls whether empty is allowed. */
export function validateEmail(value: string, label = "Email", required = false): string | null {
  const v = value.trim();
  if (v.length === 0) return required ? `${label} is required.` : null;
  if (v.length > 320 || !EMAIL_RE.test(v)) return `${label} is not a valid email address.`;
  return null;
}

/** An http(s) URL (optional unless `required`). */
export function validateUrl(value: string, label = "Website", required = false): string | null {
  const v = value.trim();
  if (v.length === 0) return required ? `${label} is required.` : null;
  if (v.length > 512 || !URL_RE.test(v)) return `${label} must be a valid http(s) URL (e.g. https://example.com).`;
  return null;
}

/**
 * Smart `https://` auto-format for a website field (as-you-type). The rule the product asks for:
 * while the user is TYPING the scheme itself, do NOT mangle it — i.e. typing "h", "ht", "http",
 * "https:/", "https://" is left untouched (it is a prefix of "https://"). An already-complete
 * scheme ("https://…" or the user's explicit "http://…") is left as-is. Only when the user DEVIATES
 * from typing a scheme (e.g. "example.com", or "wexample") do we assume they mean a host and prepend
 * "https://". An empty value stays empty. This is purely entry convenience; `validateUrl` still
 * judges the final value.
 */
export function formatWebsiteSmart(value: string): string {
  const v = value;
  if (v.trim() === "") return v;
  const lower = v.toLowerCase();
  const scheme = "https://";
  // (a) The user is partway through typing "https://" — leave it exactly as typed.
  if (scheme.startsWith(lower)) return v;
  // (b) A complete, explicit scheme (either https:// or http://) — respect it.
  if (lower.startsWith("https://") || lower.startsWith("http://")) return v;
  // (c) The user is partway through "http://" (but not matching the https prefix above) — leave it.
  if ("http://".startsWith(lower)) return v;
  // (d) Deviation from a scheme prefix → assume a host and prepend https://.
  return scheme + v.replace(/^\/+/, "");
}

/**
 * A fixed list of industry / business categories for the organization-registration dropdown. Broad,
 * jurisdiction-agnostic buckets (not a formal NAICS/SIC taxonomy) that cover most organizations; the
 * stored value is the label string. "Other" is the catch-all.
 */
export const INDUSTRY_CATEGORIES: readonly string[] = [
  "Agriculture & Farming",
  "Automotive",
  "Banking & Financial Services",
  "Construction & Real Estate",
  "Consulting & Professional Services",
  "Consumer Goods & Retail",
  "Education",
  "Energy & Utilities",
  "Government & Public Sector",
  "Healthcare & Life Sciences",
  "Hospitality, Travel & Leisure",
  "Insurance",
  "Legal Services",
  "Logistics & Transportation",
  "Manufacturing",
  "Media & Entertainment",
  "Mining & Metals",
  "Nonprofit & NGO",
  "Pharmaceuticals & Biotechnology",
  "Technology & Software",
  "Telecommunications",
  "Other",
];

/** A phone number (optional unless `required`). */
export function validatePhone(value: string, label = "Phone", required = false): string | null {
  const v = value.trim();
  if (v.length === 0) return required ? `${label} is required.` : null;
  if (!PHONE_RE.test(v)) return `${label} must be a valid phone number.`;
  return null;
}
